import { Save } from '@mui/icons-material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  type SxProps,
  Card,
  FormControl,
  FormHelperText,
  CircularProgress,
  styled,
  Stack
} from '@mui/material'
import Tab from '@mui/material/Tab'
import { Tooltip } from '@mui/material'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import React, { useState } from 'react'
import {
  createOrgAddress,
  type Address,
  type OrganizationAddress,
  updateOrgAddress,
  type Phone,
  type State,
  deleteOrgAddress,
  type Country
} from 'services'
import InputMask from 'react-input-mask'
import {
  IconButtonWithTooltip,
  StateSelect,
  AlertDialog,
  applyPhoneMask,
  getPhoneMask,
  CountryAddressSelector, fullPhone, useGlobalMessageHandler, useCountriesLuts
} from 'components'
import { createPhoneWithNullProperties, isPhoneNumberEmpty } from 'helpers'
import { formatErrorFieldMessages } from 'interfaces'
import { ToggleButton, ToggleButtonGroup } from '@mui/lab'

const CenteredCircularProgress = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
})

interface TabsCardProps {
  orgAddress: OrganizationAddress
  index: number
  deleteAddress: (id: number) => void
  reloadAddresses: () => void
}

const cardStyles: SxProps = {
  display: 'flex',
  justifyContent: 'end',
  marginTop: '12px'
}

const TabsComponent: React.FC<TabsCardProps> = ({ deleteAddress, orgAddress, index, reloadAddresses }) => {
  const [selectedTab, setSelectedTab] = useState(0)
  const [primaryAddress, setPrimaryAddress] = useState<Address>(orgAddress.primaryAddress)
  const [secondaryAddress, setSecondaryAddress] = useState<Address>(orgAddress.secondaryAddress)

  const [phone, setPhone] = useState<Phone | null>(orgAddress?.phone)
  const [hasValue, setHasValue] = useState(orgAddress?.id > 0)
  const [disabled, setDisabled] = useState(orgAddress?.id > 0)
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false)
  const [openAddressDialog, setOpenAddressDialog] = React.useState(false)
  const [isCheckedUpdateAllAddress, setIsCheckedUpdateAllAddress] = React.useState(false)
  const [isCheckedUpdateAllChildrenAddress, setIsCheckedUpdateAllChildrenAddress] = React.useState(false)
  const [loading, setLoading] = useState(false)
  const { lutValues: countriesLutValues } = useCountriesLuts()
  const { showErrorMessage, showSuccessMessage, clearMessages } = useGlobalMessageHandler()

  function buildPhone (
    phoneNumber: string | undefined | null,
    countryCode: string | undefined | null,
    extension: string | undefined | null,
    id: number | null | undefined,
    organizationId: number | undefined,
    addressId: number | undefined
  ): Phone | null {
    if (phoneNumber === null || phoneNumber?.length === 0) {
      return null
    }

    return {
      id: id ?? null,
      countryCode: countryCode ?? '',
      phoneNumber: applyPhoneMask(phoneNumber, primaryAddress.country) ?? '',
      extension: extension != null && extension.length > 0 ? extension : null,
      organizationId: organizationId ?? 0,
      addressId: addressId ?? 0
    }
  }

  const handlePhoneChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    filterKey: keyof Phone
  ): void => {
    const value = event.target.value
    setPhone((prevPhone) => {
      if (prevPhone == null) return null
      return {
        ...prevPhone,
        [filterKey]: value,
        countryCode: '',
        extension: value === '' ? '' : prevPhone.extension
      }
    })
  }

  const handlePhoneExtensionChange = (
    newExtension: string
  ): void => {
    setPhone((prevPhone) => {
      if (prevPhone == null) return null
      return {
        ...prevPhone,
        extension: newExtension
      }
    })
  }

  const handleTabChange = (_event: any, newValue: React.SetStateAction<number>): void => {
    setSelectedTab(newValue)
  }

  const handleChangeAddressTextField = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    filterKey: keyof Address,
    isSecondary: boolean = false
  ): void => {
    const value = event.target.value
    if (isSecondary) {
      setSecondaryAddress((prevAddress) => ({
        ...prevAddress,
        [filterKey]: value
      }))
    } else {
      setPrimaryAddress((prevAddress) => ({
        ...prevAddress,
        [filterKey]: value
      }))
    }
  }

  const renderAddressTextField = (
    label: string,
    filterKey: keyof Address,
    value: string = '',
    isSecondary: boolean = false
  ): JSX.Element => (
    <div>
      <TextField
        label={label}
        variant="outlined"
        disabled={disabled}
        value={value}
        onChange={(e) => {
          if (isSecondary) {
            handleChangeAddressTextField(e, filterKey, true)
          } else {
            handleChangeAddressTextField(e, filterKey, false)
          }
        }}
        fullWidth
      />
      {!isSecondary && filterKey !== 'addressTwo' && value === '' && <FormHelperText>This field is required</FormHelperText>}
      {isSecondary && filterKey !== 'addressTwo' && !isAllFieldsSecondaryFilled() && value === '' && <FormHelperText>This field is required</FormHelperText>}
    </div>
  )

  const renderSelectState = (
    state: State | null,
    isSecondary: boolean = false,
    country: Country | null
  ): JSX.Element => (
    <div>
      <StateSelect disabled={disabled} country={country} state={state} handleStateChange={(e) => {
        if (!isSecondary) {
          handleStatePrimaryChange(e)
        } else {
          handleStateSecondaryChange(e)
        }
      }} />
    </div>
  )

  const renderPhoneTextField = (
    label: string,
    filterKey: keyof Phone,
    value: string = ''
  ): JSX.Element => (
    <Grid item xs={8}>
      <InputMask
        maskChar= '-'
        mask={getPhoneMask(primaryAddress.country?.country_desc ?? '')}
        value={value}
        onChange={(e) => { handlePhoneChange(e, filterKey) }}
        disabled={disabled}
      >
        <TextField
          id="outlined-basic"
          label={label}
          variant="outlined"
          disabled={disabled}
          fullWidth
        />
      </InputMask>
    </Grid>
  )

  const renderPhoneExtensionTextField = (
    label: string,
    value: string = ''
  ): JSX.Element => (
      <Grid item xs={4}>
      <TextField
              id="phone-extension"
              label={label}
              value={value}
              onChange={(e) => { handlePhoneExtensionChange(e.target.value) }}
              variant="outlined"
              disabled={disabled || isPhoneNumberEmpty(phone) }
              fullWidth
          />
      </Grid>
  )
  const handleChangeControlLabel = (
    filterKey: keyof Address | keyof Phone,
    value: boolean | string,
    isSecondary: boolean = false
  ): void => {
    if (isSecondary) {
      setSecondaryAddress((prevAddress) => ({
        ...prevAddress,
        [filterKey]: value
      }))
      if (primaryAddress.mailingAddress) {
        setPrimaryAddress((prevAddress) => ({
          ...prevAddress,
          [filterKey]: false
        }))
      }
    } else {
      setPrimaryAddress((prevAddress) => ({
        ...prevAddress,
        [filterKey]: value
      }))
      if (secondaryAddress.mailingAddress) {
        setSecondaryAddress((prevAddress) => ({
          ...prevAddress,
          [filterKey]: false
        }))
      }
    }
  }

  const swapAddress = (): void => {
    if (!orgAddress.primaryAddress.mailingAddress) {
      [orgAddress.primaryAddress, orgAddress.secondaryAddress] = [orgAddress.secondaryAddress, orgAddress.primaryAddress]
      setPrimaryAddress(orgAddress.primaryAddress)
      setSecondaryAddress(orgAddress.secondaryAddress)
    }
  }

  const handleEditAllAddressPosition = (): void => {
    if (disabled) {
      setDisabled(false)
      return
    }

    if (isCheckedUpdateAllAddress || isCheckedUpdateAllChildrenAddress) {
      setOpenAddressDialog(true)
      return
    }
    handleEdit()
  }
  const prettierKeyNames: Record<string, string> = {
    id: 'ID',
    organizationId: 'Organization Id',
    primaryAddress: 'Primary Address',
    'primaryAddress.addressOne': 'Primary Address - Address 1',
    'primaryAddress.addressTwo': 'Primary Address - Address 2',
    'primaryAddress.city': 'Primary Address - City',
    'primaryAddress.state': 'Primary Address - State',
    'primaryAddress.country': 'Primary Address - Country',
    'primaryAddress.postalCode': 'Primary Address - Postal Code',
    'primaryAddress.mailingAddress': 'Primary Address - Mailing Address'
  }

  const handleEdit = (): void => {
    setLoading(true)
    orgAddress.primaryAddress = primaryAddress
    orgAddress.secondaryAddress = secondaryAddress
    orgAddress.phone = buildPhone(phone?.phoneNumber, phone?.countryCode, phone?.extension, phone?.id, orgAddress.organizationId, orgAddress.id)
    swapAddress()
    void updateOrgAddress(orgAddress, isCheckedUpdateAllAddress, isCheckedUpdateAllChildrenAddress)
      .then((response) => {
        if (response.succeeded) {
          showSuccessMessage('Address Edited Successfully')
          reloadAddresses()
        } else {
          showErrorMessage(formatErrorFieldMessages(response.errors, prettierKeyNames))
        }
      })
      .catch(() => {
        showErrorMessage('Error editing Address')
      })
      .finally(() => { setLoading(false) })
    setDisabled(true)
    setHasValue(true)
  }

  const handleDelete = (): void => {
    setLoading(true)
    clearMessages()
    deleteOrgAddress(orgAddress.organizationId, orgAddress.id)
      .then((response: boolean) => {
        setLoading(false)
        deleteAddress(index)
        showSuccessMessage('Address deleted sucessfully')
        reloadAddresses()
      })
      .catch((error: any) => {
        showErrorMessage('Error occurred while deleting address')
        setLoading(false)
        console.error('Error creating org:', error)
      })
    setDisabled(true)
    setHasValue(true)
  }

  const handleAdd = (): void => {
    setLoading(true)
    orgAddress.primaryAddress = primaryAddress
    orgAddress.secondaryAddress = secondaryAddress
    orgAddress.phone = buildPhone(phone?.phoneNumber, phone?.countryCode, phone?.extension, phone?.id, orgAddress.organizationId, orgAddress.id)
    swapAddress()
    createOrgAddress(orgAddress)
      .then((response) => {
        if (!response) {
          showErrorMessage('Error Creating Address')
          setLoading(false)
          return
        }
        setLoading(false)
        showSuccessMessage('Address Added Sucessfully')
        reloadAddresses()
      })
      .catch((error) => {
        showErrorMessage('Error Creating Address')
        console.log(error)
        setLoading(false)
      })
    setDisabled(true)
    setHasValue(true)
  }

  const handleOpen = (): void => {
    setOpenDeleteDialog(true)
  }

  const handleClose = (): void => {
    setOpenDeleteDialog(false)
    setOpenAddressDialog(false)
  }

  const CustomDialogContent = (): JSX.Element => (
      <IconButtonWithTooltip label="Delete Address" color="secondary" onClick={handleOpen} icon={<DeleteIcon />}/>
  )

  const isAllFieldsPrimaryFilled = (): boolean => {
    if (
      primaryAddress.addressOne === '' ||
      primaryAddress.city === '' ||
      primaryAddress?.state?.id === 0 ||
      primaryAddress?.state?.name === '' ||
      primaryAddress.postalCode === '') {
      return false
    }

    return true
  }

  const isAllFieldsSecondaryNull = (): boolean => {
    if (secondaryAddress === null) {
      return true
    }
    if (
      (secondaryAddress?.addressOne === null || (secondaryAddress?.addressOne)?.length === 0) &&
      (secondaryAddress?.city === null || (secondaryAddress?.city)?.length === 0) &&
      ((secondaryAddress?.state) == null) &&
      (secondaryAddress?.postalCode === null || (secondaryAddress?.postalCode)?.length === 0)
    ) {
      return true
    }

    return false
  }

  const isAllFieldsSecondaryFilled = (): boolean => {
    const result = isAllFieldsSecondaryNull()
    if (result) {
      return result
    }

    if (
      ((secondaryAddress?.addressOne)?.length === 0) ||
      ((secondaryAddress?.city)?.length === 0) ||
      ((secondaryAddress?.state) == null) ||
      ((secondaryAddress?.postalCode)?.length === 0)
    ) {
      return false
    }
    return true
  }

  const handleStatePrimaryChange = (state: State | null): void => {
    setPrimaryAddress((prevAddress) => ({
      ...prevAddress,
      state
    }))
  }

  const handleStateSecondaryChange = (state: State | null): void => {
    setSecondaryAddress((prevAddress) => ({
      ...prevAddress,
      state
    }))
  }

  const [selectedOptions, setSelectedOptions] = useState<string[]>([])

  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement>,
    newSelection: string[]
  ): void => {
    setSelectedOptions(newSelection)
    setIsCheckedUpdateAllAddress(newSelection.includes('allPositions'))
    setIsCheckedUpdateAllChildrenAddress(newSelection.includes('allChildren'))
  }

  return (
    <div>
     <Card>
        </Card>
        <Tabs value={selectedTab} onChange={handleTabChange} id={`simple-tab-${index}`}>
        {loading && <CenteredCircularProgress><CircularProgress size={50} /></CenteredCircularProgress>}
          <Tab label="Address 1" id={`simple-tab-${index}`} />
          <Tab label="Address 2" id={`simple-tab-${index}`} />
        </Tabs>
        <br />
        <FormControl fullWidth>
          <Grid container spacing={4}>
            <Grid item xs={4}>
              <Grid container spacing={2}>
              {renderPhoneTextField('Phone Number', 'phoneNumber', fullPhone(phone?.countryCode, phone?.phoneNumber))}
              {renderPhoneExtensionTextField('Extension', phone?.extension ?? '')}
              </Grid>
            </Grid>
             <Grid container item xs="auto">
                {index === 0 &&
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Typography variant="body2">Update address for all:</Typography>
                    <ToggleButtonGroup color="primary" value={selectedOptions} onChange={handleToggleChange} aria-label="Address update options" disabled={disabled}>
                      <ToggleButton value="allPositions">Positions from this Org</ToggleButton>
                      <ToggleButton value="allChildren">Children Orgs</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>}
              </Grid>
          </Grid>

        </FormControl>

        {selectedTab === 0 && (
          <Typography component="div" role="tabpanel" hidden={selectedTab !== 0}>
            <br />
            <FormControl fullWidth error={!isAllFieldsPrimaryFilled() && !disabled}>
              <Grid container spacing={2}>
                <Grid item xs={5}>
                  {renderAddressTextField('Address 1', 'addressOne', primaryAddress.addressOne)}
                </Grid>
                <Grid item xs={5}>
                  {renderAddressTextField('Address 2', 'addressTwo', primaryAddress.addressTwo)}
                </Grid>
                <Grid item xs={1}>
                  <FormControlLabel
                    control={<Switch
                      disabled={disabled}
                      checked={primaryAddress.mailingAddress}
                      onChange={(e) => { handleChangeControlLabel('mailingAddress', !primaryAddress.mailingAddress, false) }}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />}
                    label="Mailing Address"
                  />
                </Grid>
                <Grid item xs={2}>
                  <CountryAddressSelector countries={countriesLutValues.countries} value={primaryAddress?.country} disabled={disabled} setValue={(country) => {
                    setPrimaryAddress({ ...primaryAddress, country })
                    if (country?.id !== primaryAddress?.country?.id) {
                      setPhone(createPhoneWithNullProperties())
                    }
                  }
                  }/>
                </Grid>
                <Grid item xs={2}>
                  {renderSelectState(primaryAddress?.state, false, primaryAddress.country)}
                </Grid>
                <Grid item xs={4}>
                  {renderAddressTextField('City', 'city', primaryAddress.city)}
                </Grid>
                <Grid item xs={4}>
                  {renderAddressTextField('Postal Code', 'postalCode', primaryAddress.postalCode)}
                </Grid>
              </Grid>
            </FormControl>
          </Typography>
        )}

        {selectedTab === 1 && (
          <Typography component="div" role="tabpanel" hidden={selectedTab !== 1}>
            <br />
            <FormControl fullWidth error={!isAllFieldsPrimaryFilled() && !disabled}>
              <Grid container spacing={2}>
                <Grid item xs={5}>
                  {renderAddressTextField('Address 1', 'addressOne', secondaryAddress?.addressOne === null ? '' : secondaryAddress.addressOne, true)}
                </Grid>
                <Grid item xs={5}>
                  {renderAddressTextField('Address 2', 'addressTwo', secondaryAddress?.addressTwo === null ? '' : secondaryAddress.addressTwo, true)}
                </Grid>
                <Grid item xs={1}>
                  <Tooltip
                    title={isAllFieldsSecondaryNull() ? 'Please kindly complete the remaining fields' : ''}
                    placement="top">
                    <FormControlLabel
                      control={<Switch
                        disabled={disabled}
                        checked={secondaryAddress?.mailingAddress === null ? false : secondaryAddress.mailingAddress}
                        onChange={(e) => { handleChangeControlLabel('mailingAddress', !secondaryAddress.mailingAddress, true) }}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />}
                      label="Mailing Address"
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={2}>
                  <CountryAddressSelector countries={countriesLutValues.countries} value={secondaryAddress?.country} setValue={(country) => {
                    setSecondaryAddress({ ...secondaryAddress, country })
                  }
                  }
                    disabled={disabled}/>
                </Grid>
                <Grid item xs={2}>
                  {renderSelectState(secondaryAddress?.state, true, secondaryAddress.country)}
                </Grid>
                <Grid item xs={5}>
                  {renderAddressTextField('City', 'city', secondaryAddress?.city === null ? '' : secondaryAddress?.city, true)}
                </Grid>
                <Grid item xs={3}>
                  {renderAddressTextField('Postal Code', 'postalCode', secondaryAddress?.postalCode === null ? '' : secondaryAddress.postalCode, true)}
                </Grid>
              </Grid>
            </FormControl>
          </Typography>
        )}
      <Box sx={cardStyles}>
        <IconButtonWithTooltip
          label={hasValue ? (disabled ? 'Edit Address' : 'Save Address') : 'Add Address'}
          color="primary"
          disabled={loading || (!disabled && !isAllFieldsPrimaryFilled()) || (selectedTab === 1 && !isAllFieldsSecondaryFilled())}
          onClick={hasValue ? handleEditAllAddressPosition : handleAdd}
          icon={disabled ? <EditIcon /> : <Save />}
        />
        <br />
        <AlertDialog
          onClose={handleClose}
          open={openDeleteDialog}
          onButtonClick={handleDelete}
          title='Would you like to delete this address?'
          contentText='Are you sure you want to delete this address? If you confirm, please click on "Confirm."'
          nameButtonCancel='Close'
          nameButtonConfirm='Confirm'
          dialogContentComponent={CustomDialogContent}
        />
        <AlertDialog
          onClose={handleClose}
          open={openAddressDialog}
          onButtonClick={handleEdit}
          title='Update Address Confirmation'
          contentText={`This will update addresses for ${[
            selectedOptions.includes('allPositions') && 'all positions in this organization',
            selectedOptions.includes('allChildren') && 'the main addresses of all children organizations'
          ].filter(Boolean).join(' and ')}.`}
          nameButtonCancel='Close'
          nameButtonConfirm='Confirm'
        />
      </Box>
    </div>
  )
}

export default TabsComponent
